import type { TransformationRule, TransformationOrder, TransformationOrderItem, TransformationStats } from '@/types/transformation.types';
import { mockIngredients } from './ingredients.mock';

// Mock transformation rules - defining how ingredients can be transformed
export const mockTransformationRules: TransformationRule[] = [
  {
    id: 'rule-001',
    name: 'Pollo Crudo a Pollo Cocido',
    description: 'Cocción de pechuga de pollo para sandwiches',
    sourceIngredientId: 'ing-032', // Pollo Crudo
    targetIngredientId: 'ing-033', // Pollo Cocido
    sourceQuantity: 1,
    sourceUnit: 'kg',
    targetQuantity: 0.8,
    targetUnit: 'kg',
    conversionFactor: 0.8, // 1 kg -> 0.8 kg (pérdida por cocción)
    isActive: true,
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z'
  },
  {
    id: 'rule-002', 
    name: 'Palta a Palta Molida',
    description: 'Preparación de palta molida para sandwiches',
    sourceIngredientId: 'ing-011', // Palta
    targetIngredientId: 'ing-034', // Palta Molida
    sourceQuantity: 1,
    sourceUnit: 'kg',
    targetQuantity: 0.9,
    targetUnit: 'kg',
    conversionFactor: 0.9, // 1 kg -> 0.9 kg (pérdida por preparación)
    isActive: true,
    createdAt: '2024-01-20T09:00:00Z',
    updatedAt: '2024-01-20T09:00:00Z'
  },
  {
    id: 'rule-003',
    name: 'Pan Francés a Pan Tostado',
    description: 'Tostado de pan francés para sandwiches',
    sourceIngredientId: 'ing-001', // Pan Francés
    targetIngredientId: 'ing-035', // Pan Tostado
    sourceQuantity: 20,
    sourceUnit: 'unidad',
    targetQuantity: 20,
    targetUnit: 'unidad',
    conversionFactor: 1, // 20 unid -> 20 unid (sin pérdida)
    isActive: true,
    createdAt: '2024-01-25T11:00:00Z',
    updatedAt: '2024-01-25T11:00:00Z'
  },
  {
    id: 'rule-004',
    name: 'Jamón Entero a Jamón Rebanado',
    description: 'Rebanado de jamón para sandwiches',
    sourceIngredientId: 'ing-003', // Jamón de Pierna
    targetIngredientId: 'ing-036', // Jamón Rebanado
    sourceQuantity: 1,
    sourceUnit: 'kg',
    targetQuantity: 0.95,
    targetUnit: 'kg',
    conversionFactor: 0.95, // 1 kg -> 0.95 kg (mínima pérdida por corte)
    isActive: true,
    createdAt: '2024-02-01T08:00:00Z',
    updatedAt: '2024-02-01T08:00:00Z'
  },
  {
    id: 'rule-005',
    name: 'Papas Crudas a Papas Fritas',
    description: 'Preparación de papas fritas como acompañamiento',
    sourceIngredientId: 'ing-038', // Papas Crudas
    targetIngredientId: 'ing-037', // Papas Fritas
    sourceQuantity: 1,
    sourceUnit: 'kg',
    targetQuantity: 0.75,
    targetUnit: 'kg',
    conversionFactor: 0.75, // 1 kg -> 0.75 kg (pérdida por fritura)
    isActive: true,
    createdAt: '2024-02-05T14:00:00Z',
    updatedAt: '2024-02-05T14:00:00Z'
  }
];

// Mock transformation orders
export const mockTransformationOrders: TransformationOrder[] = [
  {
    id: 'trans-001',
    orderNumber: 'TRANS-2024-001',
    transformationRuleId: 'rule-001',
    locationId: 'loc-1',
    locationName: 'Sede Principal',
    sourceIngredientId: 'ing-032', // Pollo Crudo
    targetIngredientId: 'ing-033', // Pollo Cocido
    requestedSourceQuantity: 5,
    actualSourceQuantity: 5,
    expectedTargetQuantity: 4, // 5 * 0.8
    actualTargetQuantity: 3.9,
    efficiency: 97.5, // 3.9 / 4 * 100
    status: 'completed',
    notes: 'Cocción de pollo completada exitosamente - Actualizado',
    startedAt: '2024-01-15T10:30:00Z',
    completedAt: '2024-01-15T11:45:00Z',
    performedBy: 'Carlos López',
    approvedBy: 'Ana García',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T11:45:00Z'
  },
  {
    id: 'trans-002',
    orderNumber: 'TRANS-2024-002',
    transformationRuleId: 'rule-002',
    locationId: 'loc-2',
    locationName: 'Sucursal Norte',
    sourceIngredientId: 'ing-011', // Palta
    targetIngredientId: 'ing-034', // Palta Molida
    requestedSourceQuantity: 4,
    actualSourceQuantity: 4,
    expectedTargetQuantity: 3.6, // 4 * 0.9
    actualTargetQuantity: 3.4,
    efficiency: 94.4, // 3.4 / 3.6 * 100
    status: 'completed',
    notes: 'Palta molida lista para sandwiches',
    startedAt: '2024-01-20T09:30:00Z',
    completedAt: '2024-01-20T10:15:00Z',
    performedBy: 'María González',
    approvedBy: 'Pedro Ramírez',
    createdAt: '2024-01-20T09:00:00Z',
    updatedAt: '2024-01-20T10:15:00Z'
  },
  {
    id: 'trans-003',
    orderNumber: 'TRANS-2024-003',
    transformationRuleId: 'rule-003',
    locationId: 'loc-1',
    locationName: 'Sede Principal',
    sourceIngredientId: 'ing-001', // Pan Francés
    targetIngredientId: 'ing-035', // Pan Tostado
    requestedSourceQuantity: 50,
    actualSourceQuantity: 50,
    expectedTargetQuantity: 50, // 50 * 1
    actualTargetQuantity: 0,
    efficiency: 0,
    status: 'pending',
    notes: 'Tostando panes para el turno de almuerzo',
    startedAt: '2024-01-25T11:30:00Z',
    performedBy: 'José Torres',
    approvedBy: 'Carlos López',
    createdAt: '2024-01-25T11:00:00Z',
    updatedAt: '2024-01-25T11:30:00Z'
  },
  {
    id: 'trans-004',
    orderNumber: 'TRANS-2024-004',
    transformationRuleId: 'rule-004',
    locationId: 'loc-3',
    locationName: 'Sucursal Sur',
    sourceIngredientId: 'ing-003', // Jamón de Pierna
    targetIngredientId: 'ing-036', // Jamón Rebanado
    requestedSourceQuantity: 2,
    actualSourceQuantity: 0,
    expectedTargetQuantity: 1.9, // 2 * 0.95
    actualTargetQuantity: 0,
    efficiency: 0,
    status: 'pending',
    notes: 'Jamón listo para rebanar para sandwiches del día',
    startedAt: '2024-01-26T08:00:00Z',
    performedBy: 'Ana García',
    createdAt: '2024-01-25T16:00:00Z',
    updatedAt: '2024-01-26T08:00:00Z'
  },
  {
    id: 'trans-005',
    orderNumber: 'TRANS-2024-005',
    transformationRuleId: 'rule-005',
    locationId: 'loc-2',
    locationName: 'Sucursal Norte',
    sourceIngredientId: 'ing-038', // Papas Crudas
    targetIngredientId: 'ing-037', // Papas Fritas
    requestedSourceQuantity: 3,
    actualSourceQuantity: 0,
    expectedTargetQuantity: 2.25, // 3 * 0.75
    actualTargetQuantity: 0,
    efficiency: 0,
    status: 'deleted',
    notes: 'Papas para freír como acompañamiento',
    startedAt: '2024-01-26T10:00:00Z',
    performedBy: 'Pedro Ramírez',
    createdAt: '2024-01-26T09:30:00Z',
    updatedAt: '2024-01-26T09:30:00Z'
  }
];

// Mock transformation order items (detailed tracking)
export const mockTransformationOrderItems: TransformationOrderItem[] = [
  {
    id: 'item-001',
    orderId: 'trans-001',
    batchNumber: 'BATCH-001-A',
    sourceStockBefore: 10,
    sourceStockAfter: 5,
    targetStockBefore: 0,
    targetStockAfter: 14.5,
    transformedAt: '2024-01-15T11:45:00Z',
    transformedBy: 'Carlos López'
  },
  {
    id: 'item-002',
    orderId: 'trans-002',
    batchNumber: 'BATCH-002-A',
    sourceStockBefore: 8,
    sourceStockAfter: 4,
    targetStockBefore: 2,
    targetStockAfter: 5.4,
    transformedAt: '2024-01-20T10:15:00Z',
    transformedBy: 'María González'
  }
];

// Generate transformation stats
export const generateTransformationStats = (providedOrders?: TransformationOrder[]): TransformationStats => {
  const orders = providedOrders || mockTransformationOrders;
  
  return {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    completed: orders.filter(o => o.status === 'completed').length,
    deleted: orders.filter(o => o.status === 'deleted').length,
    totalEfficiency: orders.reduce((sum, order) => sum + order.efficiency, 0) / orders.length,
    totalValueTransformed: orders
      .filter(o => o.status === 'completed')
      .reduce((sum, order) => {
        const sourceIngredient = mockIngredients.find(ing => ing.id === order.sourceIngredientId);
        const unitCost = sourceIngredient?.globalCostPerUnit || 0;
        return sum + (order.actualSourceQuantity * unitCost);
      }, 0)
  };
};